import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../common';
import { supabase } from '../../utils/supabase';
import { getPaymentLedger, getPaymentAllocations, undoPayment } from '../../services/paymentLedgerService';
import { exportAllPaymentData } from '../../utils/exportUtils';

/**
 * PaymentLedgerView
 * 
 * Component for viewing payment history and managing payment records.
 * Features:
 * - Tabbed interface: Overview | History | By Player
 * - Payment history table with expandable rows
 * - Filtering by date, player, payment method
 * - Undo capability for payments
 * - Export functionality (future)
 */
const PaymentLedgerView = ({ clubId, clubName, playerOwed, playerBalances, sessionsWithOutstanding, highlightPaymentId }) => {
  const [payments, setPayments] = useState([]);
  const [expandedPayments, setExpandedPayments] = useState(new Set());
  const [allocationsCache, setAllocationsCache] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    playerId: '',
    startDate: '',
    endDate: '',
    paymentMethod: '',
    includeDeleted: false
  });

  // Get current user
  useEffect(() => {
    const fetchCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: player } = await supabase
          .from('players')
          .select('id')
          .eq('auth_id', user.id)
          .single();
        
        if (player) {
          setCurrentUser(player);
        }
      }
    };

    fetchCurrentUser();
  }, []);

  // Fetch payments
  const fetchPayments = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const result = await getPaymentLedger(clubId, {
      playerId: filters.playerId || undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      paymentMethod: filters.paymentMethod || undefined,
      includeDeleted: filters.includeDeleted
    });

    if (result.success) {
      setPayments(result.payments);
    } else {
      setError(result.error);
    }

    setIsLoading(false);
  }, [clubId, filters]);

  // Load payments on mount and when filters change
  useEffect(() => {
    if (clubId) {
      fetchPayments();
    }
  }, [clubId, fetchPayments]);

  // When opened with highlightPaymentId, expand that payment, load its allocations, and scroll into view
  useEffect(() => {
    if (!highlightPaymentId || payments.length === 0) return;
    const payment = payments.find(p => p.id === highlightPaymentId);
    if (!payment) return;

    setExpandedPayments(prev => new Set([...prev, highlightPaymentId]));

    if (!allocationsCache[highlightPaymentId]) {
      getPaymentAllocations(highlightPaymentId).then((result) => {
        if (result.success) {
          setAllocationsCache(prev => ({ ...prev, [highlightPaymentId]: result.allocations }));
        }
      });
    }

    const el = document.getElementById(`payment-row-${highlightPaymentId}`);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
    }
  }, [highlightPaymentId, payments]); // eslint-disable-line react-hooks/exhaustive-deps -- only run when payments load with highlight

  // Toggle payment expansion
  const togglePaymentExpansion = async (paymentId) => {
    const newExpanded = new Set(expandedPayments);
    
    if (newExpanded.has(paymentId)) {
      newExpanded.delete(paymentId);
    } else {
      newExpanded.add(paymentId);
      
      // Fetch allocations if not cached
      if (!allocationsCache[paymentId]) {
        const result = await getPaymentAllocations(paymentId);
        if (result.success) {
          setAllocationsCache(prev => ({
            ...prev,
            [paymentId]: result.allocations
          }));
        }
      }
    }
    
    setExpandedPayments(newExpanded);
  };

  // Handle undo payment
  const handleUndoPayment = async (paymentId) => {
    if (!currentUser) {
      alert('Unable to undo payment: User not authenticated');
      return;
    }

    const confirmed = window.confirm(
      'Are you sure you want to undo this payment? This will restore sessions to unpaid status and adjust the player\'s credit balance.'
    );

    if (!confirmed) return;

    const result = await undoPayment(paymentId, currentUser.id);

    if (result.success) {
      alert(`Payment undone successfully. ${result.sessionsRestored} session(s) restored to unpaid.`);
      fetchPayments();
    } else {
      alert(`Failed to undo payment: ${result.error}`);
    }
  };

  // Handle export
  const handleExport = async () => {
    setIsExporting(true);
    try {
      // Fetch all allocations for all payments
      const allAllocationsMap = {};
      
      await Promise.all(
        payments.map(async (payment) => {
          const result = await getPaymentAllocations(payment.id);
          if (result.success) {
            allAllocationsMap[payment.id] = result.allocations;
          } else {
            allAllocationsMap[payment.id] = [];
          }
        })
      );

      // Export all data
      await exportAllPaymentData({
        payments,
        allocationsMap: allAllocationsMap,
        playerOwed: playerOwed || [],
        playerBalances: playerBalances || {},
        sessionsWithOutstanding: sessionsWithOutstanding || [],
        clubName: clubName || 'club'
      });

    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export payment data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Utility functions
  const formatCurrency = (amount, currency = 'GBP') => {
    const symbols = { GBP: '£', USD: '$', EUR: '€', CAD: 'C$', AUD: 'A$' };
    return `${symbols[currency] || currency} ${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPaymentMethod = (method) => {
    const labels = {
      cash: 'Cash',
      bank_transfer: 'Bank Transfer',
      other: 'Other'
    };
    return labels[method] || method;
  };

  const getPaymentMethodBadgeColor = (method) => {
    const colors = {
      cash: 'bg-green-100 text-green-800',
      bank_transfer: 'bg-blue-100 text-blue-800',
      other: 'bg-surface-subtle text-text-secondary'
    };
    return colors[method] || colors.other;
  };

  return (
    <div className="bg-surface rounded-lg shadow">
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-border-medium">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-semibold text-text-primary">Payment Ledger</h2>
            <p className="text-sm text-text-secondary mt-1">View and manage payment history</p>
          </div>
          <Button
            type="button"
            onClick={handleExport}
            disabled={isExporting || isLoading || payments.length === 0}
            variant="primary"
            size="small"
            className="w-full sm:w-auto justify-center shrink-0"
          >
            {isExporting ? (
              <>
                <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin inline-block mr-2 align-middle" />
                Exporting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export CSV
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-6">
        {/* Error Message */}
        {error && (
          <div className="bg-danger-light border border-danger-dark/30 text-danger-dark px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Filters Section */}
        <div className="bg-surface-subtle border border-border-medium rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
            <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filters
            </h3>
            {(filters.startDate || filters.endDate || filters.paymentMethod || filters.includeDeleted) && (
              <button
                onClick={() => setFilters({
                  playerId: '',
                  startDate: '',
                  endDate: '',
                  paymentMethod: '',
                  includeDeleted: false
                })}
                className="text-xs text-primary hover:text-primary-dark font-medium self-start sm:self-auto"
              >
                Clear All
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-border-medium rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-surface"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">
                End Date
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-border-medium rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-surface"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">
                Payment Method
              </label>
              <select
                value={filters.paymentMethod}
                onChange={(e) => setFilters({ ...filters, paymentMethod: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-border-medium rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-surface"
              >
                <option value="">All Methods</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cash">Cash</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="flex items-end">
              <label className="flex items-center space-x-2 cursor-pointer pb-2">
                <input
                  type="checkbox"
                  checked={filters.includeDeleted}
                  onChange={(e) => setFilters({ ...filters, includeDeleted: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm text-text-secondary">Include deleted</span>
              </label>
            </div>
          </div>
        </div>

        {/* Payment History */}
        <div>
          {isLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-border-medium border-t-primary"></div>
                <p className="text-text-secondary mt-2">Loading payments...</p>
              </div>
            ) : payments.length === 0 ? (
              <div className="text-center py-8">
                <svg className="w-12 h-12 text-text-muted mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <p className="text-text-secondary">No payment records found</p>
                <p className="text-sm text-text-muted mt-1">Try adjusting your filters</p>
              </div>
            ) : (
              <div className="space-y-3">
                {payments.map((payment) => (
                  <div
                    key={payment.id}
                    id={`payment-row-${payment.id}`}
                    className={`border rounded-lg overflow-hidden ${
                      payment.is_deleted ? 'border-danger-dark/50 bg-danger-light' : 'border-border-medium'
                    } ${highlightPaymentId === payment.id ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                  >
                    {/* Payment Row — stacked on small screens */}
                    <div className="p-3 sm:p-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex gap-3 min-w-0 flex-1">
                        <button
                          type="button"
                          onClick={() => togglePaymentExpansion(payment.id)}
                          className="text-text-muted hover:text-text-secondary transition-colors shrink-0 mt-0.5"
                          aria-expanded={expandedPayments.has(payment.id)}
                        >
                          <svg
                            className={`w-5 h-5 transition-transform ${
                              expandedPayments.has(payment.id) ? 'rotate-90' : ''
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                        <div className="min-w-0 flex-1 space-y-2">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 justify-between">
                            <p className="text-sm font-medium text-text-primary">
                              {formatDate(payment.created_at)}
                            </p>
                            {!payment.is_deleted && (
                              <button
                                type="button"
                                onClick={() => handleUndoPayment(payment.id)}
                                className="sm:hidden px-3 py-1 text-xs text-danger-dark hover:bg-danger-light border border-danger-dark/30 rounded transition-colors"
                              >
                                Undo
                              </button>
                            )}
                          </div>
                          <p className="text-sm text-text-primary">
                            {payment.payer?.first_name} {payment.payer?.last_name}
                          </p>
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm font-semibold text-text-primary">
                              {formatCurrency(payment.amount, payment.currency_code)}
                            </p>
                            <span className={`px-2 py-1 text-xs font-medium rounded ${getPaymentMethodBadgeColor(payment.payment_method)}`}>
                              {formatPaymentMethod(payment.payment_method)}
                            </span>
                            {payment.is_deleted && (
                              <span className="px-2 py-1 text-xs font-medium rounded bg-danger-light text-danger-dark">
                                Deleted
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-text-secondary">
                            by {payment.created_by_player?.first_name} {payment.created_by_player?.last_name}
                          </p>
                        </div>
                      </div>
                      {!payment.is_deleted && (
                        <button
                          type="button"
                          onClick={() => handleUndoPayment(payment.id)}
                          className="hidden sm:block shrink-0 px-3 py-1 text-xs text-danger-dark hover:bg-danger-light border border-danger-dark/30 rounded transition-colors self-start"
                        >
                          Undo
                        </button>
                      )}
                    </div>

                    {/* Expanded Details */}
                    {expandedPayments.has(payment.id) && (
                      <div className="border-t border-border-medium bg-surface-subtle p-4 space-y-3">
                        {/* Note */}
                        {payment.note && (
                          <div>
                            <p className="text-xs font-medium text-text-secondary mb-1">Note:</p>
                            <p className="text-sm text-text-secondary">{payment.note}</p>
                          </div>
                        )}

                        {/* Allocations */}
                        {allocationsCache[payment.id] && (
                          <div>
                            <p className="text-xs font-medium text-text-secondary mb-2">
                              Applied To:
                            </p>
                            {allocationsCache[payment.id].length > 0 ? (
                              <div className="space-y-1">
                                {allocationsCache[payment.id].map((allocation) => {
                                  const sessionId = allocation.session_player?.session_id;
                                  const sessionName = allocation.session_player?.sessions?.name || 'Session';
                                  const sessionDate = allocation.session_player?.sessions?.session_date;
                                  const label = `${sessionName} - ${formatDate(sessionDate)}`;
                                  return (
                                    <div
                                      key={allocation.id}
                                      className="flex items-center justify-between text-sm bg-surface rounded p-2"
                                    >
                                      <span>
                                        {sessionId && clubId ? (
                                          <Link
                                            to={`/club/${clubId}/session-wrapup/${sessionId}`}
                                            className="text-primary hover:underline font-medium"
                                          >
                                            {label}
                                          </Link>
                                        ) : (
                                          label
                                        )}
                                      </span>
                                      <span className="font-medium">
                                        {formatCurrency(allocation.amount_applied, payment.currency_code)}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-sm text-text-muted">
                                No allocations (full amount added to credit)
                              </p>
                            )}
                          </div>
                        )}

                        {/* Deleted Info */}
                        {payment.is_deleted && (
                          <div className="bg-danger-light border border-danger-dark/30 rounded p-3">
                            <p className="text-xs text-danger-dark">
                              <strong>Deleted:</strong> {formatDate(payment.deleted_at)} by{' '}
                              {payment.deleted_by_player?.first_name} {payment.deleted_by_player?.last_name}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
      </div>
    </div>
  );
};

export default PaymentLedgerView;
